import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Helmet } from 'react-helmet';
import * as Yup from 'yup';
import { useAppAuth } from '~/hooks/appAuth';
import { useCart } from '~/hooks/cart';
import { useSettings } from '~/hooks/settings';
import IAddress from '~/interfaces/IAddress';
import IDistance from '~/interfaces/IDistance';
import openstreetmap from '~/services/openstreetmap';
import routing from '~/services/openstreetmap/routing';
import viacep from '~/services/viacep';
import getAddressFormatted from '~/utils/getAddressFormatted';
import getValidationErrors from '~/utils/getValidationErrors';

import _ from 'lodash';
interface AddProductProps {
  show: boolean;
  onClose(): void;
  distances: IDistance[];
}

interface IGeocode {
  lat: string;
  lon: string;
}

const {
  REACT_APP_ONLY_GOOGLE_MAPS,
  REACT_APP_SIMPLE_ADDRESS,
  REACT_APP_SIMPLE_ADDRESS_WITHOUT_LOCALITY,
} = process.env;

const schema = REACT_APP_SIMPLE_ADDRESS
  ? Yup.object().shape({
      road: Yup.string().required('O campo endereço é obrigatório'),
      number: Yup.string().required('O campo número é obrigatório'),
      locality: REACT_APP_SIMPLE_ADDRESS_WITHOUT_LOCALITY
        ? Yup.string()
        : Yup.string().required('O campo localidade é obrigatório'),
    })
  : Yup.object().shape({
      road: Yup.string().required('O campo endereço é obrigatório'),
      number: Yup.string().required('O campo número é obrigatório'),
      state: Yup.string().required('O campo UF é obrigatório'),
      city: Yup.string().required('O campo cidade é obrigatório'),
      neighbourhood: Yup.string().required('O campo bairro é obrigatório'),
    });

const Address: React.FC<AddProductProps> = ({
  show,
  onClose,
  distances: allDistances,
}) => {
  const { user } = useAppAuth();
  const { company } = useSettings();
  const { address, updateAddress, setAddressFilled } = useCart();
  const [data, setData] = useState({} as IAddress);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({} as any);
  const [geocode, setGeocode] = useState({} as IGeocode);
  const [locality, setLocality] = useState('');

  useEffect(() => {
    setLoading(false);
  }, [show]);

  useEffect(() => {
    if (user && user.orderAddress && !_.isEmpty(user.orderAddress)) {
      setData(user.orderAddress);
    } else if (address) {
      setData(address);
    }
  }, [address, user]);

  useEffect(() => {
    if (user && user.orderAddress && !_.isEmpty(user.orderAddress)) {
      const exists = allDistances.find(
        item => item.uid === user.orderAddress?.locality,
      );
      if (exists) {
        setLocality(user.orderAddress?.locality);
      }
    } else if (address) {
      const exists = allDistances.find(item => item.uid === address.locality);
      if (exists) {
        setLocality(address.locality);
      }
    }
  }, [address, allDistances, user]);

  const distances = useMemo(() => {
    const sorted = allDistances.sort((a, b) =>
      a.district?.toLocaleLowerCase() > b.district?.toLocaleLowerCase()
        ? 1
        : b.district?.toLocaleLowerCase() > a.district?.toLocaleLowerCase()
        ? -1
        : 0,
    );
    return sorted;
  }, [allDistances]);

  const locality_uid = useMemo(() => {
    if (user && user.orderAddress && !_.isEmpty(user.orderAddress)) {
      const findDistance = distances.find(
        item => item.district === user.orderAddress?.neighbourhood,
      );
      if (findDistance) return findDistance.uid;
    } else {
      const findDistance = distances.find(
        item => item.district === address.neighbourhood,
      );
      if (findDistance) return findDistance.uid;
    }

    return '';
  }, [distances, address, user]);

  const getDistanceOpenStreetMap = useCallback(
    async (lat1, lon1, lat2, lon2) => {
      try {
        const response = await routing.get(`${lon1},${lat1};${lon2},${lat2}`, {
          params: {
            overview: false,
            alternatives: false,
            steps: false,
          },
        });

        const [{ distance }] = response.data.routes;

        return distance;
      } catch (err) {
        console.log(err);
      }
    },
    [],
  );

  const getGeocodeAddress = useCallback(async (dataAddress: IAddress) => {
    try {
      const response = await openstreetmap.get('search', {
        params: {
          format: 'json',
          street: dataAddress.road,
          city: dataAddress.city,
          state: dataAddress.state,
        },
      });

      if (response.data.length > 0) {
        const [item] = response.data;
        const { lat, lon } = item;
        setGeocode({ lat, lon });
      }
    } catch (err) {
      console.log(err);
    }
  }, []);

  const handleChangePostcode = useCallback(
    async (event: any) => {
      try {
        const { value } = event.target;

        setData({ ...address, ...data, postcode: value });

        const parseValue = value.replace('-', '');

        if (parseValue.length === 8) {
          const response = await viacep.get(`${value}/json`);
          const { cep, logradouro, complemento, bairro, localidade, uf } =
            response.data;

          const tmp: any = {
            ...address,
            ...data,
            road: logradouro,
            neighbourhood: bairro,
            city: localidade,
            state: uf,
            complement: complemento,
            postcode: cep,
          };

          if (!REACT_APP_ONLY_GOOGLE_MAPS) {
            getGeocodeAddress(tmp as IAddress);
          }

          updateAddress(tmp);

          setData(tmp);

          ['road', 'neighbourhood', 'city', 'state', 'complement'].forEach(
            field => {
              const element = document.getElementById(field) as any;

              if (element) {
                element.value = tmp[field];
              }
            },
          );

          setErrors({});
        }
      } catch (err) {
        console.log(err);
      }
    },
    [data, updateAddress, address, getGeocodeAddress],
  );

  const handleSubmit = useCallback(async () => {
    try {
      setErrors({});

      const parseData = {
        ...address,
        ...data,
        locality,
      };

      await schema.validate(parseData, {
        abortEarly: false,
      });

      const distance = distances.find(item => item.uid === locality);
      if (distance?.status === false) {
        setErrors({
          locality: `Ops! Infelizmente o atendimento para esta localidade foi encerrado por hoje.`,
        });
        return;
      }

      let tmp = { ...parseData } as IAddress;

      if (REACT_APP_SIMPLE_ADDRESS) {
        const findDistance = distances.find(find => find.uid === locality);
        if (findDistance) {
          updateAddress({
            ...tmp,
            neighbourhood: findDistance.district,
            distance: {
              text: `km`,
              value: findDistance.value,
            },
          });
          onClose();
        } else {
          updateAddress({
            ...tmp,
            distance: {
              text: `km`,
              value: 0,
            },
          });
          onClose();
        }
      } else if (company.address) {
        if (geocode.lat && geocode.lon) {
          // METODO OPENSTREETMAP
          const getDistance = await getDistanceOpenStreetMap(
            geocode.lat,
            geocode.lon,
            company.latitude,
            company.longitude,
          );

          const distanceKm = getDistance / 1000;

          const distance = {
            text: `${parseFloat(`${distanceKm}`).toFixed(2)} km`,
            value: getDistance,
          };

          const findDistance = distances.find(find => {
            if (
              find.district &&
              `${find.district}`.toLowerCase() ===
                `${parseData.neighbourhood}`.toLowerCase()
            )
              return find;

            if (distanceKm >= find.from && distanceKm <= find.to) return find;

            if (distanceKm === 0 && distances.length > 0) return distances[0];

            return null;
          });

          console.log(findDistance);

          if (!findDistance && distances.length > 0) {
            alert('Infelizmente não atendemos o endereço informado!');
            return;
          }

          tmp = {
            ...parseData,
            distance,
          };

          updateAddress(tmp);

          onClose();
        } else {
          // METODO GOOGLE MAPS
          const service = new (
            window as any
          ).google.maps.DistanceMatrixService();
          const origins = [company.address];
          // company.latitude && company.longitude
          //   ? [`${company.latitude},${company.longitude}`]
          //   : [company.address];
          const destinations = [`${getAddressFormatted(parseData)}`];
          const request = {
            origins,
            destinations,
            travelMode: (window as any).google.maps.TravelMode.DRIVING,
            unitSystem: (window as any).google.maps.UnitSystem.METRIC,
            avoidHighways: false,
            avoidTolls: false,
          };

          service.getDistanceMatrix(request).then((response: any) => {
            const { distance } = response.rows[0].elements[0];

            const distanceKm = distance ? distance.value / 1000 : 0;

            const findDistance = distances.find(find => {
              if (
                find.district &&
                `${find.district}`.toLowerCase() ===
                  `${parseData.neighbourhood}`.toLowerCase()
              )
                return find;

              if (distanceKm >= find.from && distanceKm <= find.to) return find;

              if (distanceKm === 0 && distances.length > 0) return distances[0];

              return null;
            });

            console.log(distance);

            if (!findDistance && distances.length > 0) {
              alert('Infelizmente não atendemos o endereço informado!');
              return;
            }
            tmp = {
              ...parseData,
              distance: distance || { value: 0 },
            };
            updateAddress(tmp);
            onClose();
          });
        }
      } else {
        updateAddress(tmp);
        onClose();
      }

      setAddressFilled(true);
      // document.getElementById('checkout')?.click();
    } catch (err) {
      console.log(err);
      if (err instanceof Yup.ValidationError) {
        const parseErrors = getValidationErrors(err);
        setErrors({
          ...parseErrors,
          postcode: 'Por favor, informe outro CEP.',
        });
      }
    }
  }, [
    setAddressFilled,
    address,
    onClose,
    data,
    updateAddress,
    company,
    distances,
    getDistanceOpenStreetMap,
    geocode,
    locality,
  ]);

  const handleChangeLocality = useCallback(
    (localityId: string) => {
      setLocality(localityId);

      const distance = distances.find(item => item.uid === localityId);
      if (distance?.status === false) {
        setErrors({
          ...errors,
          locality: `Ops! Infelizmente o atendimento para esta localidade foi encerrado por hoje.`,
        });
      } else {
        setErrors({
          ...errors,
          locality: '',
        });
      }
    },
    [distances, errors],
  );

  return (
    <>
      {show && (
        <>
          <div
            className="modal fade show"
            id="addproductcart"
            tabIndex={-1}
            aria-hidden="true"
            style={{ display: 'block' }}
          >
            <div className="modal-dialog modal-sm modal-dialog-centered">
              <div className="modal-content product border-0 shadow-sm">
                <div className="modal-body">
                  <div className="row">
                    <div className="col-md-12">
                      <h5 className="mb-2">Endereço de entrega</h5>
                      {/* <button
                        type="button"
                        className="btn btn-link text-color-theme mb-3 p-0"
                        style={{ fontSize: 12, color: company.app_color }}
                        data-bs-dismiss="modal"
                        onClick={handleGeolocation}
                        disabled={loading}
                      >
                        {!loading && (
                          <>
                            <i className="bi bi-geo-alt mr-1" />
                            <span style={{ marginLeft: 4 }}>
                              Obter Localização
                            </span>
                          </>
                        )}
                        {loading && (
                          <>
                            <i className="bi bi-geo-alt mr-1" />
                            Buscando localização...
                          </>
                        )}
                      </button> */}
                      <form className="mt-3">
                        {/* <AutoComplete
                          apiKey="AIzaSyAaeuhhAZamvEONT_KbCtb_9ULCOfvof38"
                          onPlaceSelected={place => console.log(place)}
                          language="pt-BR"
                          options={{
                            types: ['address'],
                            componentRestrictions: { country: 'br' },
                          }}
                        /> */}

                        {REACT_APP_SIMPLE_ADDRESS && (
                          <>
                            <div className="form-floating mb-2 border rounded">
                              <input
                                type="text"
                                className="form-control"
                                id="road"
                                placeholder="--"
                                defaultValue={data.road}
                                onChange={event => {
                                  setData({
                                    ...data,
                                    road: event.target.value,
                                  });
                                }}
                              />
                              <label htmlFor="select">Endereço</label>
                            </div>
                            {errors.road && (
                              <p className="invalid">{errors.road}</p>
                            )}

                            <div className="form-floating mb-2 border rounded">
                              <input
                                type="text"
                                className="form-control"
                                id="reference"
                                placeholder="--"
                                defaultValue={data.number}
                                onChange={event => {
                                  setData({
                                    ...data,
                                    number: event.target.value,
                                  });
                                }}
                              />
                              <label htmlFor="select">Número</label>
                            </div>
                            {errors.number && (
                              <p className="invalid">{errors.number}</p>
                            )}

                            {!REACT_APP_SIMPLE_ADDRESS_WITHOUT_LOCALITY && (
                              <>
                                <div className="form-floating mb-2 border rounded">
                                  <select
                                    className="form-select"
                                    data-search="on"
                                    data-dropdown="xs center"
                                    onChange={ev =>
                                      handleChangeLocality(ev.target.value)
                                    }
                                    defaultValue={locality_uid}
                                  >
                                    <option value="">Selecione...</option>
                                    {distances.map(item => (
                                      <option value={item.uid}>
                                        {item.district}
                                      </option>
                                    ))}
                                  </select>
                                  <label htmlFor="select">Localidade</label>
                                </div>
                                {errors.locality && (
                                  <p className="invalid">{errors.locality}</p>
                                )}
                                {locality && (() => {
                                  const selectedDistance = distances.find(d => d.uid === locality);
                                  return selectedDistance?.observations ? (
                                    <div
                                      style={{
                                        backgroundColor: '#fff3cd',
                                        border: '1px solid #ffeaa7',
                                        borderRadius: '4px',
                                        padding: '8px 12px',
                                        marginTop: '8px',
                                        marginBottom: '8px',
                                        fontSize: '14px',
                                        color: '#856404'
                                      }}
                                    >
                                      {selectedDistance.observations}
                                    </div>
                                  ) : null;
                                })()}
                              </>
                            )}

                            <div className="form-floating mb-2 border rounded">
                              <input
                                type="text"
                                className="form-control"
                                id="reference"
                                placeholder="--"
                                defaultValue={data.reference}
                                onChange={event => {
                                  setData({
                                    ...data,
                                    reference: event.target.value,
                                  });
                                }}
                              />
                              <label htmlFor="select">
                                Ponto de Referência
                              </label>
                            </div>
                          </>
                        )}

                        {!REACT_APP_SIMPLE_ADDRESS && (
                          <>
                            <div className="form-floating mb-2 border rounded">
                              <input
                                type="text"
                                className="form-control postcode"
                                id="postcode"
                                placeholder="--"
                                defaultValue={address.postcode}
                                onChange={handleChangePostcode}
                              />
                              <label htmlFor="select">Digite o seu CEP</label>
                            </div>
                            {errors.postcode && (
                              <p className="invalid">{errors.postcode}</p>
                            )}

                            {data.postcode && data.road && (
                              <>
                                <div className="form-floating mb-2 border rounded">
                                  <input
                                    type="text"
                                    className="form-control"
                                    id="road"
                                    placeholder="--"
                                    defaultValue={address.road}
                                    onChange={event => {
                                      setData({
                                        ...data,
                                        road: event.target.value,
                                      });
                                    }}
                                  />
                                  <label htmlFor="select">Endereço</label>
                                </div>
                                {errors.road && (
                                  <p className="invalid">{errors.road}</p>
                                )}

                                <div className="form-floating mb-2 border rounded">
                                  <input
                                    type="text"
                                    className="form-control"
                                    id="number"
                                    placeholder="--"
                                    defaultValue={address.number}
                                    onChange={event => {
                                      setData({
                                        ...data,
                                        number: event.target.value,
                                      });
                                    }}
                                  />
                                  <label htmlFor="select">Número</label>
                                </div>

                                {errors.number && (
                                  <p className="invalid">{errors.number}</p>
                                )}

                                <div className="form-floating mb-2 border rounded">
                                  <input
                                    type="text"
                                    className="form-control"
                                    id="complement"
                                    placeholder="--"
                                    defaultValue={address.complement}
                                    onChange={event => {
                                      setData({
                                        ...data,
                                        complement: event.target.value,
                                      });
                                    }}
                                  />
                                  <label htmlFor="select">Complemento</label>
                                </div>

                                <div className="form-floating mb-2 border rounded">
                                  <input
                                    type="text"
                                    className="form-control"
                                    id="reference"
                                    placeholder="--"
                                    defaultValue={address.reference}
                                    onChange={event => {
                                      setData({
                                        ...data,
                                        reference: event.target.value,
                                      });
                                    }}
                                  />
                                  <label htmlFor="select">
                                    Ponto de Referência
                                  </label>
                                </div>

                                <div className="form-floating mb-2 border rounded">
                                  <input
                                    type="text"
                                    className="form-control"
                                    id="state"
                                    placeholder="--"
                                    defaultValue={address.state}
                                    onChange={event => {
                                      setData({
                                        ...data,
                                        state: event.target.value,
                                      });
                                    }}
                                  />
                                  <label htmlFor="select">UF</label>
                                </div>

                                {errors.state && (
                                  <p className="invalid">{errors.state}</p>
                                )}

                                <div className="form-floating mb-2 border rounded">
                                  <input
                                    type="text"
                                    className="form-control"
                                    id="city"
                                    placeholder="--"
                                    defaultValue={address.city}
                                    onChange={event => {
                                      setData({
                                        ...data,
                                        city: event.target.value,
                                      });
                                    }}
                                  />
                                  <label htmlFor="select">Cidade</label>
                                </div>

                                {errors.city && (
                                  <p className="invalid">{errors.city}</p>
                                )}

                                <div className="form-floating mb-2 border rounded">
                                  <input
                                    type="text"
                                    className="form-control"
                                    id="neighbourhood"
                                    placeholder="--"
                                    defaultValue={address.neighbourhood}
                                    onChange={event => {
                                      setData({
                                        ...data,
                                        neighbourhood: event.target.value,
                                      });
                                    }}
                                  />
                                  <label htmlFor="select">Bairro</label>
                                </div>

                                {errors.neighbourhood && (
                                  <p className="invalid">
                                    {errors.neighbourhood}
                                  </p>
                                )}
                              </>
                            )}
                          </>
                        )}

                        {/* <p className="invalid" style={{ lineHeight: 1 }}>
                          * Informe seu endereço, número e bairro no campo
                          abaixo para calcularmos a sua taxa de entrega.
                        </p> */}

                        {/* <div className="form-floating mb-2 border rounded">
                          <input
                            type="text"
                            className="form-control"
                            id="road"
                            placeholder="--"
                            defaultValue={address.road}
                            onChange={event => {
                              setData({
                                ...data,
                                road: event.target.value,
                              });
                            }}
                          />
                          <label htmlFor="select">Endereço</label>
                        </div>
                        {errors.road && (
                          <p className="invalid">{errors.road}</p>
                        )} */}
                      </form>
                    </div>
                  </div>
                </div>
                <div className="modal-footer justify-content-center">
                  <button
                    type="button"
                    className="btn btn-link text-color-theme"
                    onClick={handleSubmit}
                    style={{ color: company.app_color }}
                  >
                    Adicionar
                  </button>
                </div>
              </div>
            </div>
            <div
              className="modal-backdrop fade show"
              onClick={() => onClose()}
              style={{ background: '#00000060' }}
            />
          </div>

          <Helmet>
            <script src={`${process.env.PUBLIC_URL}/assets/js/masks.js`} />
          </Helmet>
        </>
      )}
    </>
  );
};

export default Address;
